/**
 * ErrorBoundary Component
 *
 * Catches React errors and displays a fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={CustomErrorFallback}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */

import { Component, type ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { attemptChunkReload, isChunkLoadError } from '@/lib/chunk-load-recovery'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: React.ComponentType<ErrorFallbackProps>
  'data-testid'?: string
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export interface ErrorFallbackProps {
  error: Error
  reset: () => void
  'data-testid'?: string
}

function DefaultErrorFallback({ error, reset, 'data-testid': testId }: ErrorFallbackProps) {
  const isStaleChunk = isChunkLoadError(error)

  const handleAction = () => {
    if (isStaleChunk) {
      attemptChunkReload() || window.location.reload()
      return
    }
    reset()
  }

  return (
    <div
      className="flex flex-col items-center justify-center h-full p-8 text-center"
      data-testid={testId}
      role="alert"
    >
      <AlertCircle className="h-12 w-12 text-red-500 mb-4" aria-hidden="true" />
      <h2 className="text-lg font-semibold mb-2">
        {isStaleChunk ? 'Update available' : 'Something went wrong'}
      </h2>
      <p className="text-sm text-muted-foreground mb-4 max-w-md">
        {isStaleChunk
          ? 'A new version of Maater was deployed. Refresh to load the latest code.'
          : error.message || 'An unexpected error occurred'}
      </p>
      <Button onClick={handleAction} variant="outline" size="sm">
        <RefreshCw className="h-4 w-4 mr-2" />
        {isStaleChunk ? 'Refresh page' : 'Try again'}
      </Button>
    </div>
  )
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const Fallback = this.props.fallback || DefaultErrorFallback
      return (
        <Fallback
          error={this.state.error}
          reset={this.reset}
          data-testid={this.props['data-testid']}
        />
      )
    }

    return this.props.children
  }
}

